import { FC, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../redux/types/types'

import styles from './StatsPageTableComponent.module.css'
import DropDown from '../DropDown/DropDown'
import { Calendar } from '../CustomCalendar/components'
import CalendarIcon from '../../assets/icons/calendar.svg'
import { format } from 'date-fns'
import { segments } from '../../constants/constants'
import { ClaimResponseType } from '../../redux/actions/types'
import { getStatsClaims } from '../../redux/actions/claimsAction'
import iconFiltersMobile from '../../assets/icons/iconFiltersMobile.svg'
import { getCompanies } from '../../redux/actions/companiesAction'
import { getPlaces } from '../../redux/actions/placesAction'
import DownloadSmet from '../DownloadSmet/DownloadSmet'
import Select from 'react-select'
import { useMediaQuery } from 'react-responsive'
import CustomButton from '../UI/CustomButton/CustomButton'
import CustomModal from '../UI/CustomModal/CustomModal'
import FilterSegment from '../FiltersSegment/FilterSegment'
import EventsFilterMenu from '../EventsFilterMenu/EventsFilterMenu'
import { parseDateSafely } from '../../utils/date'
import { useNavigate } from 'react-router-dom'
import PATH from '../../navigation/path'

interface StatsPageTableComponentProps {
  toShow?: number
  selectedCreator?: string
  selectedEstablishment?: string
  selectedPlace?: string
  selectedClient?: string
  rangeStartISO?: string | null
  rangeEndISO?: string | null
}

const initialStartDate = new Date()
initialStartDate.setFullYear(initialStartDate.getFullYear() - 1)

const initialFinishDate = new Date()
initialFinishDate.setMonth(initialFinishDate.getMonth() + 2)

const StatsPageTableComponent: FC<StatsPageTableComponentProps> = ({
  toShow,
  selectedCreator,
  selectedEstablishment,
  selectedPlace,
  selectedClient,
}) => {
  const dispatch = useAppDispatch()
  const { token } = useAppSelector((state) => state.auth)
  const { statsClaims, isLoadingStatsClaims } = useAppSelector((state) => state.claims)
  const { companies } = useAppSelector((state) => state.companies)
  const { places } = useAppSelector((state) => state.places)

  const [dataForSmet, setDataForSmet] = useState<ClaimResponseType>()
  const [openStartDate, setOpenStartDate] = useState(false)
  const [openFinishDate, setOpenFinishDate] = useState(false)
  const [openSegmentFilter, setOpenSegmentFilter] = useState(false)
  const [openRevenueFilter, setOpenRevenueFilter] = useState(false)
  const [openRevenueFilterSecond, setOpenRevenueFilterSecond] = useState(false)
  const [openEventFilter, setOpenEventFilter] = useState(false)
  const [selectedStartDate, setSelectedStartDate] = useState(initialStartDate)
  const [selectedFinishDate, setSelectedFinishDate] = useState(initialFinishDate)
  const [startDateString, setStartDateString] = useState(format(initialStartDate, 'dd.MM.yyyy'))
  const [finishDateString, setFinishDateString] = useState(format(initialFinishDate, 'dd.MM.yyyy'))
  const [activeFilterButton, setActiveFilterButton] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [activeFilteByPlace, setActiveFilteByPlace] = useState('Компания')
  const [companiesAsOption, setCompaniesAsOption] = useState<string[]>([])
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | undefined>(undefined)
  const [selectedPlaceId, setSelectedPlaceId] = useState<number | undefined>(undefined)
  const [paidFilter, setPaidFilter] = useState<boolean | undefined>(undefined)
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])

  const limit = 1000
  const navigate = useNavigate()

  const onNavigateToCurrentClaim = (id: number) => {
    navigate(`${PATH.claimCardPage}${id}`)
  }

  const fetchStatsClaims = () => {
    if (!token) return

    const dateRange = {
      start: startDateString ? format(selectedStartDate, 'yyyy-MM-dd') : undefined,
      end: finishDateString ? format(selectedFinishDate, 'yyyy-MM-dd') : undefined,
    }

    dispatch(
      getStatsClaims({
        token,
        segment: activeFilterButton || undefined,
        dateRange,
        companyId: selectedCompanyId || undefined,
        paid: paidFilter,
        categoryNames: selectedCategories.length > 0 ? selectedCategories : undefined,
        userId: selectedCreator || undefined,
        clientId: selectedClient ? Number(selectedClient) : undefined,
        place: selectedPlace ? selectedPlace : undefined,
        establishmentId: selectedEstablishment ? Number(selectedEstablishment) : undefined,
      }),
    )
  }

  const claimsData = Array.isArray(statsClaims) ? statsClaims : statsClaims?.[0] || []

  const paginatedClaims = claimsData

  const handleDownloadSmet = async () => {
    if (claimsData && claimsData.length > 0) {
      setDataForSmet(claimsData[0])
    }
  }

  useEffect(() => {
    handleDownloadSmet()
  }, [claimsData])

  useEffect(() => {
    setCurrentPage(1)
  }, [toShow])

  useEffect(() => {
    if (companies && companies.companies.length > 0) {
      const comp = companies.companies.map((company) => company.name)
      setCompaniesAsOption(comp)
    }
  }, [companies])

  const isMobile = useMediaQuery({ query: '(max-width: 767px)' })

  useEffect(() => {
    fetchStatsClaims()
  }, [
    token,
    activeFilterButton,
    selectedStartDate,
    selectedFinishDate,
    selectedCompanyId,
    selectedPlaceId,
    selectedPlace,
    paidFilter,
    selectedCategories,
    selectedCreator,
    selectedClient,
    selectedEstablishment,
  ])

  useEffect(() => {
    if (token) {
      dispatch(getCompanies({ itemsPerPage: 100, page: 1, token }))
      dispatch(getPlaces({ itemsPerPage: 1000, page: 1, token }))
    }
  }, [token, dispatch])

  const openStartDateDropDown = (e: any) => {
    e.stopPropagation()
    setOpenStartDate(!openStartDate)
  }

  const onChangeStartDate = (date: Date) => {
    setSelectedStartDate(date)
    const newDateStr = format(date, 'dd.MM.yyyy')
    setStartDateString(newDateStr)
    setOpenStartDate(false)
    setCurrentPage(1)
  }

  const openFinishDateDropDown = (e: any) => {
    e.stopPropagation()
    setOpenFinishDate(!openFinishDate)
  }

  const onChangeFinishDate = (date: Date) => {
    setSelectedFinishDate(date)
    const newDateStr = format(date, 'dd.MM.yyyy')
    setFinishDateString(newDateStr)
    setOpenFinishDate(false)
    setCurrentPage(1)
  }

  useEffect(() => {
    setCurrentPage(1)
  }, [activeFilterButton])

  const getFilteredByCompany = (companyId: number | undefined) => {
    setSelectedCompanyId(companyId)
    setCurrentPage(1)
  }

  const getFilteredByPlace = (placeId: number | undefined) => {
    setSelectedPlaceId(placeId)
    setCurrentPage(1)
  }

  const resetFilters = () => {
    setSelectedStartDate(initialStartDate)
    setSelectedFinishDate(initialFinishDate)
    setStartDateString(format(initialStartDate, 'dd.MM.yyyy'))
    setFinishDateString(format(initialFinishDate, 'dd.MM.yyyy'))
    setActiveFilterButton('')
    setSelectedCompanyId(undefined)
    setSelectedPlaceId(undefined)
    setPaidFilter(undefined)
    setSelectedCategories([])
    setCurrentPage(1)
  }

  return (
    <div className={styles.tableContainer}>
      <CustomModal visible={openSegmentFilter} setVisible={setOpenSegmentFilter}>
        <FilterSegment
          resetFilters={
            <button onClick={resetFilters} className={styles.resetFilters}>
              Сбросить фильтры
            </button>
          }
          segments={segments}
          setActiveFilterButton={setActiveFilterButton}
          dateBlock={
            <div className={styles.mobileDateContainer}>
              <h3>Период</h3>
              <div className={styles.mobileDateContent}>
                <div className={styles.dateLeftBlock}>
                  <div className={styles.dateBlockTextes}>
                    <h4 className={styles.subtitleMobile}>от</h4>
                    <div className={styles.dateContainer} onClick={(e) => openStartDateDropDown(e)}>
                      <img className={styles.dateIcon} src={CalendarIcon} alt='calendarIcon' />
                      <div className={styles.dateValue}>{startDateString || ' '}</div>
                    </div>
                  </div>
                  <DropDown
                    className={styles.dropDown}
                    open={openStartDate}
                    style={{ left: '5px' }}
                  >
                    <Calendar
                      selectedDate={selectedStartDate}
                      selectDate={(date) => onChangeStartDate(date)}
                    />
                  </DropDown>
                </div>
                <div className={styles.dateRightBlock}>
                  <div className={styles.dateBlockTextes}>
                    <h4 className={styles.subtitleMobile}>до</h4>
                    <div
                      className={styles.dateContainer}
                      onClick={(e) => openFinishDateDropDown(e)}
                    >
                      <img className={styles.dateIcon} src={CalendarIcon} alt='calendarIcon' />
                      <div className={styles.dateValue}>{finishDateString || ''}</div>
                    </div>
                  </div>
                  <DropDown className={styles.dropDown} open={openFinishDate}>
                    <Calendar
                      selectedDate={selectedFinishDate}
                      selectDate={(date) => onChangeFinishDate(date)}
                    />
                  </DropDown>
                </div>
              </div>
            </div>
          }
          onClickHandler={() => setOpenSegmentFilter(false)}
        />
      </CustomModal>

      <CustomModal visible={openRevenueFilter} setVisible={setOpenRevenueFilter}>
        <FilterSegment
          resetFilters={
            <button onClick={resetFilters} className={styles.resetFilters}>
              Сбросить фильтры
            </button>
          }
          revenue={['Полученная выручка', 'Отмененная выручка', 'Все']}
          setActiveRevenue={(selected) => {
            if (selected === 'Полученная выручка') {
              setPaidFilter(true)
            } else if (selected === 'Все') {
              setPaidFilter(undefined)
            } else if (selected === 'Отмененная выручка') {
              setPaidFilter(false)
            }
          }}
          dateBlock={
            <div className={styles.mobileDateContainer}>
              <h3>Период</h3>
              <div className={styles.mobileDateContent}>
                <div className={styles.dateLeftBlock}>
                  <div className={styles.dateBlockTextes}>
                    <h4 className={styles.subtitleMobile}>от</h4>
                    <div className={styles.dateContainer} onClick={(e) => openStartDateDropDown(e)}>
                      <img className={styles.dateIcon} src={CalendarIcon} alt='calendarIcon' />
                      <div className={styles.dateValue}>{startDateString || ' '}</div>
                    </div>
                  </div>
                  <DropDown
                    style={{ left: '5px' }}
                    className={styles.dropDown}
                    open={openStartDate}
                  >
                    <Calendar
                      selectedDate={selectedStartDate}
                      selectDate={(date) => onChangeStartDate(date)}
                    />
                  </DropDown>
                </div>
                <div className={styles.dateRightBlock}>
                  <div className={styles.dateBlockTextes}>
                    <h4 className={styles.subtitleMobile}>до</h4>
                    <div
                      className={styles.dateContainer}
                      onClick={(e) => openFinishDateDropDown(e)}
                    >
                      <img className={styles.dateIcon} src={CalendarIcon} alt='calendarIcon' />
                      <div className={styles.dateValue}>{finishDateString || ''}</div>
                    </div>
                  </div>
                  <DropDown className={styles.dropDown} open={openFinishDate}>
                    <Calendar
                      selectedDate={selectedFinishDate}
                      selectDate={(date) => onChangeFinishDate(date)}
                    />
                  </DropDown>
                </div>
              </div>
            </div>
          }
          onClickHandler={() => setOpenRevenueFilter(false)}
        />
      </CustomModal>
      <CustomModal visible={openRevenueFilterSecond} setVisible={setOpenRevenueFilterSecond}>
        <FilterSegment
          resetFilters={
            <button onClick={resetFilters} className={styles.resetFilters}>
              Сбросить фильтры
            </button>
          }
          revenue={['Доп.уборка', 'Мобильный бар']}
          setActiveRevenue={(selected) => {
            if (selected === 'Доп.уборка') {
              setSelectedCategories(['Доп.уборка'])
            } else if (selected === 'Мобильный бар') {
              setSelectedCategories(['Мобильный бар'])
            }
          }}
          dateBlock={
            <div className={styles.mobileDateContainer}>
              <h3>Период</h3>
              <div className={styles.mobileDateContent}>
                <div className={styles.dateLeftBlock}>
                  <div className={styles.dateBlockTextes}>
                    <h4 className={styles.subtitleMobile}>от</h4>
                    <div className={styles.dateContainer} onClick={(e) => openStartDateDropDown(e)}>
                      <img className={styles.dateIcon} src={CalendarIcon} alt='calendarIcon' />
                      <div className={styles.dateValue}>{startDateString || ' '}</div>
                    </div>
                  </div>
                  <DropDown
                    style={{ left: '5px' }}
                    className={styles.dropDown}
                    open={openStartDate}
                  >
                    <Calendar
                      selectedDate={selectedStartDate}
                      selectDate={(date) => onChangeStartDate(date)}
                    />
                  </DropDown>
                </div>
                <div className={styles.dateRightBlock}>
                  <div className={styles.dateBlockTextes}>
                    <h4 className={styles.subtitleMobile}>до</h4>
                    <div
                      className={styles.dateContainer}
                      onClick={(e) => openFinishDateDropDown(e)}
                    >
                      <img className={styles.dateIcon} src={CalendarIcon} alt='calendarIcon' />
                      <div className={styles.dateValue}>{finishDateString || ''}</div>
                    </div>
                  </div>
                  <DropDown className={styles.dropDown} open={openFinishDate}>
                    <Calendar
                      selectedDate={selectedFinishDate}
                      selectDate={(date) => onChangeFinishDate(date)}
                    />
                  </DropDown>
                </div>
              </div>
            </div>
          }
          onClickHandler={() => setOpenRevenueFilterSecond(false)}
        />
      </CustomModal>
      <CustomModal visible={openEventFilter} setVisible={setOpenEventFilter}>
        <EventsFilterMenu
          resetFilters={
            <button onClick={resetFilters} className={styles.resetFilters}>
              Сбросить фильтры
            </button>
          }
          companiesOnChange={(selectedOption: any) => {
            if (!selectedOption || selectedOption === '') {
              getFilteredByCompany(undefined)
            } else {
              getFilteredByCompany(Number(selectedOption))
            }
          }}
          setActiveFilteByPlace={(selected: string) => setActiveFilteByPlace(selected)}
          placesOnChange={(selectedOption) => {
            if (!selectedOption || selectedOption === '') {
              getFilteredByPlace(undefined)
            } else {
              getFilteredByPlace(Number(selectedOption))
            }
          }}
          companies={[
            { value: '', label: 'Все компании' },
            ...companies.companies.map((company) => ({
              value: company.id,
              label: company.name,
            })),
          ]}
          places={[
            { value: '', label: 'Все Залы' },
            ...places.places.map((place) => ({
              value: place.id,
              label: place.name,
            })),
          ]}
          dateBlock={
            <div className={styles.mobileDateContainer}>
              <h3>Период</h3>
              <div className={styles.mobileDateContent}>
                <div className={styles.dateLeftBlock}>
                  <div className={styles.dateBlockTextes}>
                    <h4 className={styles.subtitleMobile}>от</h4>
                    <div className={styles.dateContainer} onClick={(e) => openStartDateDropDown(e)}>
                      <img className={styles.dateIcon} src={CalendarIcon} alt='calendarIcon' />
                      <div className={styles.dateValue}>{startDateString || ' '}</div>
                    </div>
                  </div>
                  <DropDown
                    style={{ left: '5px' }}
                    className={styles.dropDown}
                    open={openStartDate}
                  >
                    <Calendar
                      selectedDate={selectedStartDate}
                      selectDate={(date) => onChangeStartDate(date)}
                    />
                  </DropDown>
                </div>
                <div className={styles.dateRightBlock}>
                  <div className={styles.dateBlockTextes}>
                    <h4 className={styles.subtitleMobile}>до</h4>
                    <div
                      className={styles.dateContainer}
                      onClick={(e) => openFinishDateDropDown(e)}
                    >
                      <img className={styles.dateIcon} src={CalendarIcon} alt='calendarIcon' />
                      <div className={styles.dateValue}>{finishDateString || ''}</div>
                    </div>
                  </div>
                  <DropDown className={styles.dropDown} open={openFinishDate}>
                    <Calendar
                      selectedDate={selectedFinishDate}
                      selectDate={(date) => onChangeFinishDate(date)}
                    />
                  </DropDown>
                </div>
              </div>
            </div>
          }
          onClickHandler={() => setOpenEventFilter(false)}
        />
      </CustomModal>
      {toShow == 0 && (
        <div className={styles.tableButtonGroup}>
          {isMobile ? (
            <CustomButton
              title=''
              iconSrc={iconFiltersMobile}
              className={styles.filtersBtn}
              onClick={() => setOpenSegmentFilter(true)}
            />
          ) : (
            <div className={styles.buttonsContainer}>
              <div className={styles.filterButtonsBlock}>
                <Select
                  className={styles.selectInput}
                  options={[
                    ...segments.map((value) => ({
                      label: value,
                      value: value,
                    })),
                  ]}
                  placeholder='Выберите'
                  onChange={(selectedOption) => {
                    if (selectedOption) {
                      setActiveFilterButton(selectedOption?.value)
                    }
                  }}
                  classNamePrefix='custom-select'
                  menuPlacement='bottom'
                  menuPosition='absolute'
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      border: 'none',
                      boxShadow: 'none',
                      minHeight: 'auto',
                      backgroundColor: 'transparent',
                    }),
                    menu: (provided) => ({
                      ...provided,
                      right: 0,
                      overflow: 'auto',
                    }),
                    menuList: (provided) => ({
                      ...provided,
                      height: '100%',
                      maxHeight: '250px',
                      overflowY: 'auto',
                    }),
                    input: (provided) => ({
                      ...provided,
                      margin: 0,
                      padding: 0,
                    }),
                    valueContainer: (provided) => ({
                      ...provided,
                      padding: 0,
                    }),
                    dropdownIndicator: (provided) => ({
                      ...provided,
                      padding: 0,
                    }),
                    clearIndicator: (provided) => ({
                      ...provided,
                      padding: 0,
                    }),
                    indicatorSeparator: () => ({
                      display: 'none',
                    }),
                  }}
                />
              </div>
              <div className={styles.dateBlock}>
                <div className={styles.dateLeftBlock}>
                  <div className={styles.dateBlockTextes}>
                    <h4 className={styles.subtitle}>Период: от</h4>
                    <div className={styles.dateContainer} onClick={(e) => openStartDateDropDown(e)}>
                      <img className={styles.dateIcon} src={CalendarIcon} alt='calendarIcon' />
                      <div className={styles.dateValue}>{startDateString || ' '}</div>
                    </div>
                  </div>
                  <DropDown className={styles.dropDown} open={openStartDate}>
                    <Calendar
                      disableDayRestrictions={true}
                      selectedDate={selectedStartDate}
                      selectDate={(date) => onChangeStartDate(date)}
                    />
                  </DropDown>
                </div>
                <div className={styles.dateRightBlock}>
                  <div className={styles.dateBlockTextes}>
                    <h4 className={styles.subtitle}>до</h4>
                    <div
                      className={styles.dateContainer}
                      onClick={(e) => openFinishDateDropDown(e)}
                    >
                      <img className={styles.dateIcon} src={CalendarIcon} alt='calendarIcon' />
                      <div className={styles.dateValue}>{finishDateString || ''}</div>
                    </div>
                  </div>
                  <DropDown className={styles.dropDown} open={openFinishDate}>
                    <Calendar
                      disableDayRestrictions={true}
                      selectedDate={selectedFinishDate}
                      selectDate={(date) => onChangeFinishDate(date)}
                    />
                  </DropDown>
                </div>
              </div>
              <button onClick={resetFilters} className={styles.resetFilters}>
                Сбросить фильтры
              </button>
            </div>
          )}
          <div onClick={handleDownloadSmet}>
            <DownloadSmet data={claimsData} />
          </div>
        </div>
      )}

      {toShow == 0 && (
        <table className={styles.tableContainer}>
          <thead className={styles.tableHeader}>
            <tr className={styles.tableHeaderRow}>
              <th className={styles.headerItem}>Дата</th>
              <th className={styles.headerItem}>Клиент</th>
              <th className={styles.headerItem}>Зал</th>
              <th className={styles.headerItem}>Полученная выручка</th>
              <th className={styles.headerItem}>Отмененная выручка</th>
            </tr>
          </thead>
          <tbody className={styles.tableBody}>
            {paginatedClaims.length > 0 ? (
              paginatedClaims.map((claim: ClaimResponseType, index: number) => (
                <tr
                  onClick={() => onNavigateToCurrentClaim(claim?.id)}
                  key={claim?.id || index}
                  className={styles.tableBodyRow}
                >
                  <td className={styles.bodyItem}>
                    {claim?.startDate
                      ? format(parseDateSafely(claim?.startDate), 'dd.MM.yyyy')
                      : '-'}{' '}
                    - {claim?.endDate ? format(parseDateSafely(claim?.endDate), 'dd.MM.yyyy') : '-'}
                  </td>
                  <td className={styles.bodyItem}>
                    {claim?.client?.fullName || 'Неизвестный клиент'}
                  </td>
                  <td className={styles.bodyItem}>{claim?.place?.name || 'Неизвестный зал'}</td>
                  <td className={styles.bodyItem}>
                    {claim?.paid || claim?.prepaymentAmount || 0} руб.
                  </td>
                  <td className={styles.bodyItem}>
                    {claim?.status === 'canceled' ? claim?.price : 0}
                  </td>
                </tr>
              ))
            ) : (
              <tr className={styles.tableHeaderRow}>
                <td style={{ padding: '10px' }} colSpan={5}>
                  Нет еще таких заявок
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {toShow == 1 && (
        <div className={styles.tableButtonGroup}>
          {isMobile ? (
            <CustomButton
              title=''
              iconSrc={iconFiltersMobile}
              className={styles.filtersBtn}
              onClick={() => setOpenRevenueFilter(true)}
            />
          ) : (
            <div className={styles.buttonsContainer}>
              <div className={styles.filterButtonsBlock}>
                <Select
                  className={styles.selectInput}
                  options={[
                    { label: 'Полученная выручка', value: true },
                    { label: 'Отмененная выручка', value: false },
                    { label: 'Все', value: undefined },
                  ]}
                  placeholder='Полученная выручка'
                  onChange={(selectedOption) => {
                    if (selectedOption) {
                      setPaidFilter(selectedOption?.value)
                    }
                  }}
                  classNamePrefix='custom-select'
                  menuPlacement='bottom'
                  menuPosition='absolute'
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      border: 'none',
                      boxShadow: 'none',
                      minHeight: 'auto',
                      backgroundColor: 'transparent',
                    }),
                    menu: (provided) => ({
                      ...provided,
                      right: 0,
                      overflow: 'auto',
                    }),
                    menuList: (provided) => ({
                      ...provided,
                      height: '100%',
                      maxHeight: '250px',
                      overflowY: 'auto',
                    }),
                    input: (provided) => ({
                      ...provided,
                      margin: 0,
                      padding: 0,
                    }),
                    valueContainer: (provided) => ({
                      ...provided,
                      padding: 0,
                    }),
                    dropdownIndicator: (provided) => ({
                      ...provided,
                      padding: 0,
                    }),
                    clearIndicator: (provided) => ({
                      ...provided,
                      padding: 0,
                    }),
                    indicatorSeparator: () => ({
                      display: 'none',
                    }),
                  }}
                />
              </div>
              <div className={styles.dateBlock}>
                <div className={styles.dateLeftBlock}>
                  <div className={styles.dateBlockTextes}>
                    <h4 className={styles.subtitle}>Период: от</h4>
                    <div className={styles.dateContainer} onClick={(e) => openStartDateDropDown(e)}>
                      <img className={styles.dateIcon} src={CalendarIcon} alt='calendarIcon' />
                      <div className={styles.dateValue}>{startDateString || ' '}</div>
                    </div>
                  </div>
                  <DropDown className={styles.dropDown} open={openStartDate}>
                    <Calendar
                      disableDayRestrictions={true}
                      selectedDate={selectedStartDate}
                      selectDate={(date) => onChangeStartDate(date)}
                    />
                  </DropDown>
                </div>
                <div className={styles.dateRightBlock}>
                  <div className={styles.dateBlockTextes}>
                    <h4 className={styles.subtitle}>до</h4>
                    <div
                      className={styles.dateContainer}
                      onClick={(e) => openFinishDateDropDown(e)}
                    >
                      <img className={styles.dateIcon} src={CalendarIcon} alt='calendarIcon' />
                      <div className={styles.dateValue}>{finishDateString || ''}</div>
                    </div>
                  </div>
                  <DropDown className={styles.dropDown} open={openFinishDate}>
                    <Calendar
                      disableDayRestrictions={true}
                      selectedDate={selectedFinishDate}
                      selectDate={(date) => onChangeFinishDate(date)}
                    />
                  </DropDown>
                </div>
              </div>
              <button
                onClick={resetFilters}
                style={{
                  padding: '4px 16px',
                  backgroundColor: 'transparent',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#6b7280',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                Сбросить фильтры
              </button>
            </div>
          )}

          <div onClick={handleDownloadSmet}>
            <DownloadSmet data={claimsData} />
          </div>
        </div>
      )}

      {toShow == 1 && (
        <table className={styles.tableContainer}>
          <thead className={styles.tableHeader}>
            <tr className={styles.tableHeaderRow}>
              <th className={styles.headerItem}>Дата</th>
              <th className={styles.headerItem}>Клиент</th>
              <th className={styles.headerItem}>Зал</th>
              <th className={styles.headerItem}>Еда и напитки</th>
              <th className={styles.headerItem}>Алкогольные напитки</th>
              <th className={styles.headerItem}>Сервис</th>
              <th className={styles.headerItem}>Пробковый сбор</th>
              <th className={styles.headerItem}>Аренда площадки</th>
              <th className={styles.headerItem}>Всего</th>
            </tr>
          </thead>
          <tbody className={styles.tableBody}>
            {paginatedClaims.length > 0 ? (
              paginatedClaims.map((claim: ClaimResponseType, index: number) => (
                <tr
                  onClick={() => onNavigateToCurrentClaim(claim?.id)}
                  key={claim?.id || index}
                  className={styles.tableBodyRow}
                >
                  <td className={styles.bodyItem}>
                    {claim?.startDate
                      ? format(parseDateSafely(claim?.startDate), 'dd.MM.yyyy')
                      : '-'}{' '}
                    - {claim?.endDate ? format(parseDateSafely(claim?.endDate), 'dd.MM.yyyy') : '-'}
                  </td>
                  <td className={styles.bodyItem}>
                    {claim?.client?.fullName || 'Неизвестный клиент'}
                  </td>
                  <td className={styles.bodyItem}>{claim?.place?.name || 'Неизвестный зал'}</td>
                  <td className={styles.bodyItem}>
                    {(() => {
                      const foodTotal =
                        claim?.order?.orderDetails
                          ?.filter((p) => p?.category?.type === 'food')
                          .reduce((acc, p) => acc + p.price * p.quantity, 0) || 0
                      return foodTotal > 0 ? `${foodTotal} руб.` : '0 руб.'
                    })()}
                  </td>
                  <td className={styles.bodyItem}>
                    {(() => {
                      const alcoholTotal =
                        claim?.order?.orderDetails
                          ?.filter((p) => p?.category?.type === 'alcohol')
                          .reduce((acc, p) => acc + p.price * p.quantity, 0) || 0
                      return alcoholTotal > 0 ? `${alcoholTotal} руб.` : '0 руб.'
                    })()}
                  </td>
                  <td className={styles.bodyItem}>
                    {(() => {
                      const foodTotal =
                        claim?.order?.orderDetails
                          ?.filter((p) => p?.category?.type === 'food')
                          .reduce((acc, p) => acc + p.price * p.quantity, 0) || 0
                      const serviceTotal = claim?.serviceFeeAmount
                        ? Math.ceil(claim?.serviceFeeAmount)
                        : claim?.serviceFeePercent
                        ? Math.ceil((foodTotal * claim?.serviceFeePercent) / 100)
                        : 0
                      return `${serviceTotal} руб.`
                    })()}
                  </td>
                  <td className={styles.bodyItem}>
                    {(() => {
                      const alcoholTotal =
                        claim?.order?.orderDetails
                          ?.filter((p) => p?.category?.type === 'alcohol')
                          .reduce((acc, p) => acc + p.price * p.quantity, 0) || 0
                      const corkFeeTotal = claim?.corkFeeAmount
                        ? claim?.corkFeeAmount
                        : claim?.corkFeePercent
                        ? Math.round((alcoholTotal * claim?.corkFeePercent) / 100)
                        : 0
                      return `${corkFeeTotal} руб.`
                    })()}
                  </td>
                  <td className={styles.bodyItem}>
                    {claim?.place?.establishment?.rentCost || '0'} руб.
                  </td>
                  <td className={styles.bodyItem}>
                    {paidFilter == true ? claim.paid + 'руб' : claim.price + 'руб'}
                  </td>
                </tr>
              ))
            ) : (
              <tr className={styles.tableHeaderRow}>
                <td style={{ padding: '10px' }} colSpan={9}>
                  Нет еще таких заявок
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {toShow == 2 && (
        <div className={styles.tableButtonGroup}>
          {isMobile ? (
            <CustomButton
              title=''
              iconSrc={iconFiltersMobile}
              className={styles.filtersBtn}
              onClick={() => setOpenRevenueFilterSecond(true)}
            />
          ) : (
            <div className={styles.buttonsContainer}>
              <div className={styles.filterButtonsBlock}>
                <Select
                  className={styles.selectInput}
                  options={[
                    { label: 'Мобильный бар', value: ['Мобильный бар'] },
                    { label: 'Доп.уборка', value: ['Доп.уборка'] },
                  ]}
                  placeholder='Полученная выручка'
                  onChange={(selectedOption) => {
                    if (selectedOption) {
                      setSelectedCategories(selectedOption.value)
                    }
                  }}
                  classNamePrefix='custom-select'
                  menuPlacement='bottom'
                  menuPosition='absolute'
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      border: 'none',
                      boxShadow: 'none',
                      minHeight: 'auto',
                      backgroundColor: 'transparent',
                    }),
                    menu: (provided) => ({
                      ...provided,
                      right: 0,
                      overflow: 'auto',
                    }),
                    menuList: (provided) => ({
                      ...provided,
                      height: '100%',
                      maxHeight: '250px',
                      overflowY: 'auto',
                    }),
                    input: (provided) => ({
                      ...provided,
                      margin: 0,
                      padding: 0,
                    }),
                    valueContainer: (provided) => ({
                      ...provided,
                      padding: 0,
                    }),
                    dropdownIndicator: (provided) => ({
                      ...provided,
                      padding: 0,
                    }),
                    clearIndicator: (provided) => ({
                      ...provided,
                      padding: 0,
                    }),
                    indicatorSeparator: () => ({
                      display: 'none',
                    }),
                  }}
                />
              </div>
              <div className={styles.dateBlock}>
                <div className={styles.dateLeftBlock}>
                  <div className={styles.dateBlockTextes}>
                    <h4 className={styles.subtitle}>Период: от</h4>
                    <div className={styles.dateContainer} onClick={(e) => openStartDateDropDown(e)}>
                      <img className={styles.dateIcon} src={CalendarIcon} alt='calendarIcon' />
                      <div className={styles.dateValue}>{startDateString || ' '}</div>
                    </div>
                  </div>
                  <DropDown className={styles.dropDown} open={openStartDate}>
                    <Calendar
                      disableDayRestrictions={true}
                      selectedDate={selectedStartDate}
                      selectDate={(date) => onChangeStartDate(date)}
                    />
                  </DropDown>
                </div>
                <div className={styles.dateRightBlock}>
                  <div className={styles.dateBlockTextes}>
                    <h4 className={styles.subtitle}>до</h4>
                    <div
                      className={styles.dateContainer}
                      onClick={(e) => openFinishDateDropDown(e)}
                    >
                      <img className={styles.dateIcon} src={CalendarIcon} alt='calendarIcon' />
                      <div className={styles.dateValue}>{finishDateString || ''}</div>
                    </div>
                  </div>
                  <DropDown className={styles.dropDown} open={openFinishDate}>
                    <Calendar
                      disableDayRestrictions={true}
                      selectedDate={selectedFinishDate}
                      selectDate={(date) => onChangeFinishDate(date)}
                    />
                  </DropDown>
                </div>
              </div>{' '}
              <button
                onClick={resetFilters}
                style={{
                  padding: '4px 16px',
                  backgroundColor: 'transparent',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#6b7280',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                Сбросить фильтры
              </button>
            </div>
          )}
          <div onClick={handleDownloadSmet}>
            <DownloadSmet data={statsClaims} />
          </div>
        </div>
      )}
      {toShow == 2 && (
        <table className={styles.tableContainer}>
          <thead className={styles.tableHeader}>
            <tr className={styles.tableHeaderRow}>
              <th className={styles.headerItem}>Дата</th>
              <th className={styles.headerItem}>Клиент</th>
              <th className={styles.headerItem}>Зал</th>
              <th className={styles.headerItem}>Полученная выручка, руб.</th>
              <th className={styles.headerItem}>Отмененная выручка, руб.</th>
            </tr>
          </thead>
          <tbody className={styles.tableBody}>
            {paginatedClaims.length > 0 ? (
              paginatedClaims.map((claim: ClaimResponseType, index: number) => (
                <tr
                  onClick={() => onNavigateToCurrentClaim(claim?.id)}
                  key={claim?.id || index}
                  className={styles.tableBodyRow}
                >
                  <td className={styles.bodyItem}>
                    {claim?.startDate
                      ? format(parseDateSafely(claim?.startDate), 'dd.MM.yyyy')
                      : '-'}{' '}
                    - {claim?.endDate ? format(parseDateSafely(claim?.endDate), 'dd.MM.yyyy') : '-'}
                  </td>
                  <td className={styles.bodyItem}>
                    {claim?.client?.fullName ||
                      claim?.client?.organizationName ||
                      'Неизвестный клиент'}
                  </td>
                  <td className={styles.bodyItem}>{claim?.place?.name}</td>
                  <td className={styles.bodyItem}>{claim?.paid} руб.</td>
                  <td className={styles.bodyItem}>
                    {claim?.status == 'rejected' ? claim?.price : '0 руб.'}
                  </td>
                </tr>
              ))
            ) : (
              <tr className={styles.tableHeaderRow}>
                <td style={{ padding: '10px' }} colSpan={5}>
                  Нет еще таких заявок
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {toShow == 3 && (
        <div className={styles.tableButtonGroup}>
          {isMobile ? (
            <CustomButton
              title=''
              iconSrc={iconFiltersMobile}
              className={styles.filtersBtn}
              onClick={() => setOpenEventFilter(true)}
            />
          ) : (
            <div className={styles.buttonsContainer}>
              <div className={styles.filterButtonsBlock}>
                <Select
                  className={styles.selectInput}
                  value={{
                    value: activeFilteByPlace,
                    label: activeFilteByPlace === 'Компания' ? 'Компания' : 'Зал',
                  }}
                  options={[
                    { value: 'Компания', label: 'Компания' },
                    { value: 'Зал', label: 'Зал' },
                  ]}
                  onChange={(selectedOption) => {
                    if (selectedOption) {
                      setActiveFilteByPlace(selectedOption.value)
                      setSelectedPlaceId(undefined)
                      setSelectedCompanyId(undefined)
                      setCurrentPage(1)
                    }
                  }}
                  classNamePrefix='custom-select'
                  menuPlacement='bottom'
                  menuPosition='absolute'
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      border: 'none',
                      boxShadow: 'none',
                      minHeight: 'auto',
                      backgroundColor: 'transparent',
                    }),
                    menu: (provided) => ({
                      ...provided,
                      right: 0,
                      overflow: 'auto',
                    }),
                    menuList: (provided) => ({
                      ...provided,
                      height: '100%',
                      maxHeight: '250px',
                      overflowY: 'auto',
                    }),
                    input: (provided) => ({
                      ...provided,
                      margin: 0,
                      padding: 0,
                    }),
                    valueContainer: (provided) => ({
                      ...provided,
                      padding: 0,
                    }),
                    dropdownIndicator: (provided) => ({
                      ...provided,
                      padding: 0,
                    }),
                    clearIndicator: (provided) => ({
                      ...provided,
                      padding: 0,
                    }),
                    indicatorSeparator: () => ({
                      display: 'none',
                    }),
                  }}
                />

                {activeFilteByPlace === 'Компания' ? (
                  <Select
                    className={styles.selectInput}
                    value={
                      selectedCompanyId
                        ? {
                            value: selectedCompanyId,
                            label:
                              companies.companies.find((c) => c.id === selectedCompanyId)?.name ||
                              '',
                          }
                        : { value: '', label: 'Все компании' }
                    }
                    onChange={(selectedOption) => {
                      if (!selectedOption || selectedOption.value === '') {
                        getFilteredByCompany(undefined)
                      } else {
                        getFilteredByCompany(Number(selectedOption.value))
                      }
                    }}
                    options={[
                      { value: '', label: 'Все компании' },
                      ...companies.companies.map((company) => ({
                        value: company.id,
                        label: company.name,
                      })),
                    ]}
                    placeholder='Выберите компанию'
                    classNamePrefix='custom-select'
                    menuPlacement='bottom'
                    menuPosition='absolute'
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        border: 'none',
                        boxShadow: 'none',
                        minHeight: 'auto',
                        backgroundColor: 'transparent',
                      }),
                      menu: (provided) => ({
                        ...provided,
                        right: 0,
                        overflow: 'auto',
                      }),
                      menuList: (provided) => ({
                        ...provided,
                        height: '100%',
                        maxHeight: '250px',
                        overflowY: 'auto',
                      }),
                      input: (provided) => ({
                        ...provided,
                        margin: 0,
                        padding: 0,
                      }),
                      valueContainer: (provided) => ({
                        ...provided,
                        padding: 0,
                      }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        padding: 0,
                      }),
                      clearIndicator: (provided) => ({
                        ...provided,
                        padding: 0,
                      }),
                      indicatorSeparator: () => ({
                        display: 'none',
                      }),
                    }}
                  />
                ) : (
                  <Select
                    className={styles.selectInput}
                    value={
                      selectedPlaceId
                        ? {
                            value: selectedPlaceId,
                            label: places.places.find((p) => p.id === selectedPlaceId)?.name || '',
                          }
                        : { value: '', label: 'Все Залы' }
                    }
                    onChange={(selectedOption) => {
                      if (!selectedOption || selectedOption.value === '') {
                        getFilteredByPlace(undefined)
                      } else {
                        getFilteredByPlace(Number(selectedOption.value))
                      }
                    }}
                    options={[
                      { value: '', label: 'Все Залы' },
                      ...places.places.map((place) => ({
                        value: place.id,
                        label: place.name,
                      })),
                    ]}
                    placeholder='Выберите зал'
                    classNamePrefix='custom-select'
                    menuPlacement='bottom'
                    menuPosition='absolute'
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        border: 'none',
                        boxShadow: 'none',
                        minHeight: 'auto',
                        backgroundColor: 'transparent',
                      }),
                      menu: (provided) => ({
                        ...provided,
                        right: 0,
                        overflow: 'auto',
                      }),
                      menuList: (provided) => ({
                        ...provided,
                        height: '100%',
                        maxHeight: '250px',
                        overflowY: 'auto',
                      }),
                      input: (provided) => ({
                        ...provided,
                        margin: 0,
                        padding: 0,
                      }),
                      valueContainer: (provided) => ({
                        ...provided,
                        padding: 0,
                      }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        padding: 0,
                      }),
                      clearIndicator: (provided) => ({
                        ...provided,
                        padding: 0,
                      }),
                      indicatorSeparator: () => ({
                        display: 'none',
                      }),
                    }}
                  />
                )}
              </div>
              <div className={styles.dateBlock}>
                <div className={styles.dateLeftBlock}>
                  <div className={styles.dateBlockTextes}>
                    <h4 className={styles.subtitle}>Период: от</h4>
                    <div className={styles.dateContainer} onClick={(e) => openStartDateDropDown(e)}>
                      <img className={styles.dateIcon} src={CalendarIcon} alt='calendarIcon' />
                      <div className={styles.dateValue}>{startDateString || ' '}</div>
                    </div>
                  </div>
                  <DropDown className={styles.dropDown} open={openStartDate}>
                    <Calendar
                      disableDayRestrictions={true}
                      selectedDate={selectedStartDate}
                      selectDate={(date) => onChangeStartDate(date)}
                    />
                  </DropDown>
                </div>
                <div className={styles.dateRightBlock}>
                  <div className={styles.dateBlockTextes}>
                    <h4 className={styles.subtitle}>до</h4>
                    <div
                      className={styles.dateContainer}
                      onClick={(e) => openFinishDateDropDown(e)}
                    >
                      <img className={styles.dateIcon} src={CalendarIcon} alt='calendarIcon' />
                      <div className={styles.dateValue}>{finishDateString || ''}</div>
                    </div>
                  </div>
                  <DropDown className={styles.dropDown} open={openFinishDate}>
                    <Calendar
                      disableDayRestrictions={true}
                      selectedDate={selectedFinishDate}
                      selectDate={(date) => onChangeFinishDate(date)}
                    />
                  </DropDown>
                </div>
              </div>{' '}
              <button
                onClick={resetFilters}
                style={{
                  padding: '4px 16px',
                  backgroundColor: 'transparent',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#6b7280',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                Сбросить фильтры
              </button>
            </div>
          )}
          <div onClick={handleDownloadSmet}>
            <DownloadSmet data={statsClaims} />
          </div>
        </div>
      )}
      {toShow == 3 && (
        <table className={styles.tableContainer}>
          <thead className={styles.tableHeader}>
            <tr className={styles.tableHeaderRow}>
              <th className={styles.headerItem}>Дата</th>
              <th className={styles.headerItem}>
                {activeFilteByPlace == 'Зал' ? 'Зал' : 'Компания'}
              </th>
              <th className={styles.headerItem}>Полученная выручка</th>
            </tr>
          </thead>
          <tbody className={styles.tableBody}>
            {paginatedClaims.length > 0 ? (
              paginatedClaims.map((claim: ClaimResponseType, index: number) => (
                <tr
                  onClick={() => onNavigateToCurrentClaim(claim?.id)}
                  key={claim?.id || index}
                  className={styles.tableBodyRow}
                >
                  <td className={styles.bodyItem}>
                    {claim?.startDate
                      ? format(parseDateSafely(claim?.startDate), 'dd.MM.yyyy')
                      : '-'}{' '}
                    - {claim?.endDate ? format(parseDateSafely(claim?.endDate), 'dd.MM.yyyy') : '-'}
                  </td>
                  <td className={styles.bodyItem}>
                    {activeFilteByPlace == 'Зал' ? claim?.place?.name : claim?.place?.company?.name}
                  </td>
                  <td className={styles.bodyItem}>{claim?.paid || 0} руб.</td>
                </tr>
              ))
            ) : (
              <tr className={styles.tableHeaderRow}>
                <td style={{ padding: '10px' }} colSpan={5}>
                  Нет еще таких заявок
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  )
}

export default StatsPageTableComponent
